import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import InvoiceApi, {
	getFutureAndGeneratedInvoicesQueryKey,
	getUseInvoiceQueryKey,
	useFutureAndGeneratedInvoices,
} from '../../api/InvoiceApi';
import { getUsePolicyQueryKey } from '../../api/PolicyApi';
import { getInvoicesForPolicyholderQueryKey } from '../../api/PolicyholderApi';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { PolicyContext, PolicyData } from '../../context/policy';
import {
	PAY_INVOICE_FAILURE,
	PAY_INVOICE_SUCCESS,
	WRITE_OFF_INVOICE_FAILURE,
	WRITE_OFF_INVOICE_SUCCESS,
} from '../../data/labelsAndMessages';
import {
	PaymentCreateRequest,
	PolicyInvoice2Response,
} from '../../types/policy';
import PaymentsList from '../PaymentsList';
import { errorToast, successToast } from '../Toast';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	policyData: PolicyData;
	onPayment?: (locator: string, req: PaymentCreateRequest) => void;
}

const InvoiceView = (props: Props) => {
	const params = useParams();
	const { authData, policyData } = props;
	const { policy, loadData, setLoading } = policyData;
	const queryClient = useQueryClient();
	const authToken = authData?.auth?.authorizationToken;

	const { data: invoices } = useFutureAndGeneratedInvoices(
		policy.locator,
		authToken,
		authData?.hasPermission('policy.fetch'),
	);
	const [invoice, setInvoice] = useState<PolicyInvoice2Response>(null);

	useEffect(() => {
		if (invoices) {
			const foundInvoice = invoices?.generatedInvoices?.find(
				(i) => i.invoice.displayId === params.invoiceId,
			);

			if (foundInvoice) {
				setInvoice(foundInvoice.invoice);
			}
		}
	}, [invoices, params.invoiceId]);

	const refetchData = () => {
		// invalidate policy
		queryClient.invalidateQueries(
			getUsePolicyQueryKey(policy.locator, authToken),
		);

		// invalidate invoices
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(policy.locator, authToken),
		);
		queryClient.invalidateQueries(
			getInvoicesForPolicyholderQueryKey(authToken, policy.policyholderLocator),
		);

		// invalidate invoice
		queryClient.invalidateQueries(
			getUseInvoiceQueryKey(authToken, invoice.locator),
		);
	};

	const payInvoice = async (
		_policyLocator: string,
		locator: string,
		req: any,
	) => {
		setLoading(true);
		try {
			await InvoiceApi.payInvoice(
				authData.auth.authorizationToken,
				locator,
				req,
			);
			refetchData();
			successToast(PAY_INVOICE_SUCCESS());
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(PAY_INVOICE_FAILURE());
		}

		try {
			await loadData(policy.locator);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const reversePayment = async (paymentLocator: string) => {
		setLoading(true);
		try {
			await InvoiceApi.reversePayment(
				authData.auth.authorizationToken,
				paymentLocator,
			);
			refetchData();
			successToast('Successfully reversed payment');
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to pay invoice');
		} finally {
			setLoading(false);
		}
	};

	const writeOffInvoice = async (locator: string) => {
		setLoading(true);
		try {
			await InvoiceApi.writeOffInvoice(
				authData.auth.authorizationToken,
				locator,
			);
			refetchData();
			successToast(WRITE_OFF_INVOICE_SUCCESS());
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(WRITE_OFF_INVOICE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	return (
		<PaymentsList
			invoice={invoice}
			onPayment={payInvoice}
			onReverse={reversePayment}
			onWriteOff={writeOffInvoice}
			callback={null}
			currency={policy?.currency}
		/>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData' | 'policyData'>) => (
		<ConfigContext.Consumer>
			{(configData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<PolicyContext.Consumer>
							{(policyData) => (
								<Component
									{...props}
									configData={configData}
									authData={authData}
									policyData={policyData}
								/>
							)}
						</PolicyContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ConfigContext.Consumer>
	);
}

export default withContext(InvoiceView);
